/**
 * SaveForLaterDialog — keep a critical alert on the bar, muted, until later.
 *
 * Owner ruling on review fmbdf6 (2026-09-25): the CSR can mark a critical
 * alert "save for later"; it changes colour on the bar, optionally carries a
 * come-back date, and returns to critical with a note once that date passes.
 */
import { useState } from 'react';
import type { NotificationItem } from '../../services/notifications';
import styles from './SaveForLaterDialog.module.css';

export const SAVE_NOTE_MAX = 500;

interface SaveForLaterDialogProps {
  alert: NotificationItem;
  submitting?: boolean;
  error?: string | null;
  onCancel: () => void;
  /** `until` is an ISO date (YYYY-MM-DD) or null for "until I bring it back". */
  onSubmit: (until: string | null, note: string) => void;
}

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function SaveForLaterDialog({
  alert,
  submitting = false,
  error = null,
  onCancel,
  onSubmit,
}: SaveForLaterDialogProps) {
  const [until, setUntil] = useState<string>('');
  const [note, setNote] = useState('');
  const tooLong = note.length > SAVE_NOTE_MAX;
  const dateInPast = until !== '' && until < todayIso();
  const canSubmit = !submitting && !tooLong && !dateInPast;

  return (
    <div
      className={styles.overlay}
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-for-later-title"
      data-testid="save-for-later-dialog"
    >
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h2 id="save-for-later-title" className={styles.title}>
          Save for later
        </h2>
        <p className={styles.alertTitle}>{alert.title}</p>
        <p className={styles.message}>
          It stays on the bar in a quieter colour so you don't lose it. The morning report will
          know you have it. Pick a date and it turns red again that day after.
        </p>

        <label className={styles.field}>
          <span className={styles.label}>Come back to it on (optional)</span>
          <input
            type="date"
            data-testid="save-for-later-date"
            className={styles.input}
            value={until}
            min={todayIso()}
            onChange={(e) => setUntil(e.target.value)}
            disabled={submitting}
          />
          {dateInPast && (
            <span className={styles.hint} data-testid="save-for-later-date-hint">
              Pick today or a later date.
            </span>
          )}
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Short note (optional)</span>
          <textarea
            data-testid="save-for-later-note"
            className={styles.textarea}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. waiting for the customer's credit app"
            disabled={submitting}
          />
          {tooLong && (
            <span className={styles.hint} data-testid="save-for-later-note-hint">
              Keep the note under {SAVE_NOTE_MAX} characters.
            </span>
          )}
        </label>

        {error && (
          <p className={styles.error} role="alert" data-testid="save-for-later-error">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            data-testid="save-for-later-submit"
            className={styles.submit}
            onClick={() => onSubmit(until || null, note.trim())}
            disabled={!canSubmit}
          >
            {submitting ? 'Saving…' : 'Save for later'}
          </button>
        </div>
      </div>
    </div>
  );
}
